import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Stack,
  Chip,
  Alert,
  CircularProgress,
  Divider,
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import {
  restaurantActiveSaleApi,
  RestaurantActiveSaleDto,
} from '../../../../services/restaurantApiService';
import { loadLocalPrinterSettings } from './LocalPrinterSettingsModal';
import {
  printAdisyonReceipt,
  AdisyonReceiptItem,
} from './AdisyonPrintService';
import salesService from '../../../../services/salesService';

interface PavoPaymentClosedInfoModalProps {
  open: boolean;
  sale: RestaurantActiveSaleDto | null;
  tableName?: string;
  companyId: string | null;
  quickSaleScreenId: number | null;
  /** Web tarafında onayı veren kullanıcı (denetim için backend'e gider) */
  confirmedByName?: string | null;
  confirmedById?: number | null;
  onClose: () => void;
  /** Tamam başarılı sonrası tetiklenir (parent listeyi yeniler). */
  onConfirmed: () => void;
}

const COLOR_DARK_PURPLE = '#6A1B9A';

const formatTL = (amount?: number | null): string => {
  if (amount == null || isNaN(amount)) return '-';
  return new Intl.NumberFormat('tr-TR', {
    style: 'currency',
    currency: 'TRY',
    minimumFractionDigits: 2,
  }).format(amount);
};

const formatDateTime = (iso?: string | null): string => {
  if (!iso) return '-';
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return new Intl.DateTimeFormat('tr-TR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }).format(d);
  } catch {
    return iso;
  }
};

const cashierTypeLabel = (type?: string | null): string => {
  switch ((type || '').toLowerCase()) {
    case 'waiter':
      return 'Garson';
    case 'cashier':
      return 'Kasiyer';
    default:
      return 'Kullanıcı';
  }
};

const paymentMethodLabel = (method?: string | null): string => {
  if (!method) return '-';
  const m = method.toLowerCase();
  if (m === 'cash' || m === 'nakit') return 'Nakit';
  if (m === 'pos' || m.includes('kredi')) return 'Kredi Kartı';
  if (m === 'partial' || m === 'karma') return 'Karma (Nakit + POS)';
  if (m === 'openaccount' || m === 'open_account' || m === 'acik hesap') return 'Açık Hesap';
  return method;
};

const PavoPaymentClosedInfoModal: React.FC<PavoPaymentClosedInfoModalProps> = ({
  open,
  sale,
  tableName,
  companyId,
  quickSaleScreenId,
  confirmedByName,
  confirmedById,
  onClose,
  onConfirmed,
}) => {
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [reprintLoading, setReprintLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!sale) return null;

  const handleConfirm = async () => {
    if (!sale.Id) return;
    setError(null);
    setConfirmLoading(true);
    try {
      const result = await restaurantActiveSaleApi.confirmPavoClose(sale.Id, {
        ConfirmedByCashierName: confirmedByName ?? null,
        ConfirmedByCashierId: confirmedById ?? null,
      });
      if (!result.Success) {
        setError(result.Message || 'Onaylama başarısız');
        return;
      }
      // Idempotent: AlreadyClosed da başarı sayılır
      onConfirmed();
      onClose();
    } catch (e: any) {
      setError(e?.message || 'Beklenmeyen bir hata oluştu');
    } finally {
      setConfirmLoading(false);
    }
  };

  const handleReprint = async () => {
    if (!sale.Id) return;
    setError(null);
    setReprintLoading(true);
    try {
      const result = await restaurantActiveSaleApi.reprintPavoReceipt(sale.Id);
      if (!result.Success || !result.Data) {
        setError(result.Message || 'Fiş bilgisi alınamadı');
        return;
      }
      // Restoran API'si PascalCase döner; camelCase de destekle.
      const raw: any = result.Data;
      const pick = (a: string, b: string) => raw[a] ?? raw[b];
      const data = {
        tableName: pick('tableName', 'TableName') as string | undefined,
        saleNumber: pick('saleNumber', 'SaleNumber') as string | undefined,
        subTotal: pick('subTotal', 'SubTotal'),
        discountAmount: pick('discountAmount', 'DiscountAmount'),
        taxAmount: pick('taxAmount', 'TaxAmount'),
        totalAmount: pick('totalAmount', 'TotalAmount'),
        itemsJson: pick('itemsJson', 'ItemsJson') as string | undefined,
        completedAt: pick('completedAt', 'CompletedAt') as string | undefined,
        completedByName: pick('completedByName', 'CompletedByName') as string | undefined,
        openedByName: pick('openedByName', 'OpenedByName') as string | undefined,
        reservedShortCode: pick('reservedShortCode', 'ReservedShortCode') as string | undefined,
      };

      if (!companyId || !quickSaleScreenId) {
        setError('Yazıcı için şirket/ekran bilgisi yok');
        return;
      }

      // ✅ Normal adisyon akışıyla aynı ayar çözümü:
      // merkezi ekran ayarları (PrintingSettingsJson) + yerel override (enabled ise).
      let printingMode: 'single' | 'category' = 'category';
      let defaultPrinter: { id: string; name: string } | null = null;
      let adisyonPrinter: { id: string; name: string } | null = null;
      let localServiceUrl = 'http://localhost:3003';
      let receiptDisplaySettings: any = null;
      try {
        const screenData: any = await salesService.getQuickSaleScreenById(quickSaleScreenId);
        localServiceUrl = screenData?.LocalServiceUrl || localServiceUrl;
        if (screenData?.PrintingSettingsJson) {
          const ps = JSON.parse(screenData.PrintingSettingsJson);
          printingMode = ps.printingMode || printingMode;
          defaultPrinter = ps.defaultReceiptPrinter || null;
          adisyonPrinter = ps.adisyonPrinter || null;
          receiptDisplaySettings = ps.receiptDisplaySettings || null;
        }
      } catch (e) {
        console.warn('[PavoReprint] Merkezi yazıcı ayarları alınamadı:', e);
      }
      const localSettings = loadLocalPrinterSettings(companyId, quickSaleScreenId);
      if (localSettings?.enabled) {
        printingMode = localSettings.printingMode || printingMode;
        defaultPrinter = localSettings.defaultReceiptPrinter || defaultPrinter;
        adisyonPrinter = localSettings.adisyonPrinter || adisyonPrinter;
      }

      // Yazıcı seçimi — handlePrintCheck (Adisyon Yazdır) ile aynı mantık:
      // tek yazıcı modu → varsayılan yazıcı, kategori modu → adisyon yazıcısı.
      let printer: { id: string; name: string } | null = null;
      if (printingMode === 'single' && defaultPrinter) {
        printer = defaultPrinter;
      } else if (printingMode === 'category' && adisyonPrinter) {
        printer = adisyonPrinter;
      } else if (defaultPrinter) {
        printer = defaultPrinter;
      }
      if (!printer) {
        setError(
          'Yazıcı tanımlanmamış. Ekran ayarlarındaki Yazdırma sekmesinden veya restoran ekranındaki "Yazıcı Ayarları" bölümünden adisyon yazıcısı seçin.',
        );
        return;
      }

      // ItemsJson parse
      let parsedItems: AdisyonReceiptItem[] = [];
      try {
        const arr = data.itemsJson ? JSON.parse(data.itemsJson) : [];
        if (Array.isArray(arr)) {
          parsedItems = arr.map((it: any) => ({
            name: it.productName ?? it.ProductName ?? 'Ürün',
            quantity: Number(it.quantity ?? it.Quantity ?? 0),
            unitPrice: Number(it.unitPrice ?? it.UnitPrice ?? 0),
            totalPrice: Number(it.totalPrice ?? it.TotalPrice ?? 0),
            vatRate: Number(it.vatRate ?? it.VatRate ?? 0) || undefined,
          }));
        }
      } catch {
        // bozuk JSON — sessiz geç, yine de boş items ile yazdır
      }

      const completedAt = new Date(data.completedAt ?? '');
      const dateStr = isNaN(completedAt.getTime())
        ? '-'
        : new Intl.DateTimeFormat('tr-TR', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
          }).format(completedAt);
      const timeStr = isNaN(completedAt.getTime())
        ? '-'
        : new Intl.DateTimeFormat('tr-TR', {
            hour: '2-digit',
            minute: '2-digit',
          }).format(completedAt);

      const discountAmt = Number(data.discountAmount) || 0;

      await printAdisyonReceipt(
        {
          invoiceNumber: data.saleNumber || `ADİSYON-${sale.Id}`,
          date: dateStr,
          time: timeStr,
          tableName: data.tableName || tableName || sale.TableName || `Masa ${sale.TableId}`,
          items: parsedItems,
          subtotal: Number(data.subTotal) || 0,
          vatTotal: Number(data.taxAmount) || 0,
          grandTotal: Number(data.totalAmount) || 0,
          waiterName: data.completedByName || data.openedByName || undefined,
          orderNumber: data.reservedShortCode || undefined,
          companyInfo: {
            name: receiptDisplaySettings?.companyName || '',
            address: receiptDisplaySettings?.companyAddress || '',
            taxNumber: receiptDisplaySettings?.taxNumber || '',
          },
          standardFooterNote: receiptDisplaySettings?.standardFooterNote || '',
          thankYouMessage:
            receiptDisplaySettings?.thankYouMessage ||
            'Bizi tercih ettiğiniz için teşekkür ederiz!',
          headerType: receiptDisplaySettings?.headerType || 'text',
          logoUrl: receiptDisplaySettings?.logoUrl || '',
          showDiscountInfo: discountAmt > 0,
          discountAmount: discountAmt,
          discountType: 'amount',
        },
        { id: printer.id, name: printer.name },
        localServiceUrl,
      );
    } catch (e: any) {
      setError(e?.message || 'Yazdırma başarısız');
    } finally {
      setReprintLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={!confirmLoading && !reprintLoading ? onClose : undefined}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle
        sx={{
          backgroundColor: COLOR_DARK_PURPLE,
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <PointOfSaleIcon />
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h6" sx={{ lineHeight: 1.2 }}>
            Pavo Cihazından Ödendi
          </Typography>
          <Typography variant="caption" sx={{ opacity: 0.9 }}>
            {tableName || sale.TableName || `Masa ${sale.TableId}`}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        <Stack spacing={2}>
          <Alert severity="info" icon={<PointOfSaleIcon />}>
            Bu masa Pavo cihazından kapatıldı. Aşağıdaki bilgileri kontrol edip
            <strong> "Tamam"</strong> butonuyla onaylayın. Onayladıktan sonra masa
            açık masalar listesinden kaldırılacak.
          </Alert>

          <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
            <Stack spacing={1.2}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Ödemeyi Alan
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Chip
                    label={cashierTypeLabel(sale.PavoPaymentCashierType)}
                    size="small"
                    color="secondary"
                    variant="outlined"
                  />
                  <Typography variant="body1" fontWeight={600}>
                    {sale.PavoPaymentCashierName || '-'}
                  </Typography>
                </Box>
              </Box>

              <Divider />

              <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Tahsilat
                </Typography>
                <Typography
                  variant="h6"
                  sx={{ color: COLOR_DARK_PURPLE, fontWeight: 700 }}
                >
                  {formatTL(sale.PavoPaymentAmount)}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Ödeme Yöntemi
                </Typography>
                <Typography variant="body2" fontWeight={500}>
                  {paymentMethodLabel(sale.PavoPaymentMethod)}
                </Typography>
              </Box>

              <Divider />

              <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Tarih
                </Typography>
                <Typography variant="body2">
                  {formatDateTime(sale.PavoPaymentClosedAt)}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Pavo Satış No
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                  {sale.PavoPaymentSaleNumber || '-'}
                </Typography>
              </Box>
            </Stack>
          </Box>

          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1, justifyContent: 'space-between' }}>
        <Button
          onClick={handleReprint}
          startIcon={reprintLoading ? <CircularProgress size={18} /> : <PrintIcon />}
          disabled={confirmLoading || reprintLoading}
          variant="outlined"
        >
          Fişi Yeniden Yazdır
        </Button>

        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            onClick={onClose}
            startIcon={<CloseIcon />}
            disabled={confirmLoading || reprintLoading}
            color="inherit"
          >
            Kapat
          </Button>
          <Button
            onClick={handleConfirm}
            startIcon={confirmLoading ? <CircularProgress size={18} /> : <CheckIcon />}
            disabled={confirmLoading || reprintLoading}
            variant="contained"
            sx={{
              backgroundColor: COLOR_DARK_PURPLE,
              '&:hover': { backgroundColor: '#4A148C' },
            }}
          >
            Tamam
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default PavoPaymentClosedInfoModal;
